import { useContext, useEffect, useRef } from 'react';

import AccountContext from 'contexts/AccountContext';
import BaseViewsContext from 'contexts/BaseViewsContext';
import CardContext from 'contexts/CardContext';
import ComparisonContext from 'contexts/ComparisonContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import { InsightsContentContext } from 'contextProviders/InsightsContentProvider';
import InsightsViewScopeContext from 'contextProviders/InsightsContentProvider/InsightsViewScopeContext';
import useDateScope from 'hooks/useDateScope';

import buildGadgetScopeFragment from './buildGadgetScopeFragment';

// Captures a single dashboard gadget's resolved scope (its own date range +
// effective filters) for the Insights agent. A dashboard has no view-level scope
// — each gadget resolves its own deep in the tree — so this must run INSIDE the
// gadget's DashboardGadgetDateScopeProvider + DashboardGadgetProvider, where
// useDateScope resolves that gadget's range and FilterPlatesContext exposes its
// effective filters.
//
// No-ops wherever there's no Insights view scope (reports set it too, but only
// dashboards register scopes; a null entityId — the Explore modal, gadget
// previews, Storybook, a popup report over a dashboard — skips registration). The
// getter is registered once and reads through a ref, so a settling refetch (which
// rebuilds dateScope / drillDownsAsFixed identity) never re-registers.
const useRegisterDashboardGadgetScope = (): void => {
  const { entityId } = useContext(InsightsViewScopeContext);
  const { i: cardId } = useContext(CardContext);
  const { registerGadgetScope } = useContext(InsightsContentContext);

  const { scopeAsFixed, drillDownsAsFixed } = useContext(FilterPlatesContext);
  const { currentComparison } = useContext(ComparisonContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { weekStartsOn: accountWeekStartsOn } = useContext(AccountContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  // Empty args, matching RegisterReportInsightsView: this resolves the gadget's
  // PRIMARY range; the comparison is described separately below.
  const dateScope = useDateScope({});

  const getScope = () =>
    buildGadgetScopeFragment({
      dateScope,
      scopeAsFixed,
      drillDownsAsFixed,
      baseViews,
      comparison: currentComparison,
      weekStartsOn: weekStartsOnOverride ?? accountWeekStartsOn,
    });
  const getScopeRef = useRef(getScope);
  getScopeRef.current = getScope;

  useEffect(() => {
    if (!entityId || !cardId) {
      return undefined;
    }
    return registerGadgetScope({
      entityId,
      cardId,
      getScope: () => getScopeRef.current(),
    });
  }, [entityId, cardId, registerGadgetScope]);
};

export default useRegisterDashboardGadgetScope;
